import axiosInstance from '../../core/axiosConfig.ts';
import * as Sentry from '@sentry/react';

export type DeliveryProvider = 'WOLT' | 'GLOVO' | 'DELIVERY_HERO' | 'INDEPENDENT';

/** How each service is named on screen. The server sends the same names; this covers the order board. */
export const PROVIDER_NAMES: Record<DeliveryProvider, string> = {
    WOLT: 'Wolt',
    GLOVO: 'Glovo',
    DELIVERY_HERO: 'Delivery Hero',
    INDEPENDENT: 'Our couriers',
};

export const providerName = (provider: string) =>
    PROVIDER_NAMES[provider as DeliveryProvider] ?? provider;

export type DeliveryPriorityEntry = {
    provider: DeliveryProvider;
    name: string;
    /** False for a service Vello has no integration with yet: dispatch skips it. */
    connected: boolean;
    enabled: boolean;
    position: number;
};

export type CityDeliveryPriority = {
    id: number;
    name: string;
    countryCode: string;
    isActive: boolean;
    restaurantCount: number;
    woltMappedCount: number;
    /** False while the city runs on the default order. */
    customised: boolean;
    entries: DeliveryPriorityEntry[];
    /** The Telegram group for this city's "needs a courier" alerts; null sends them to the default chat. */
    telegramChatId: string | null;
};

export default class DeliveryPriorityService {

    async list(): Promise<CityDeliveryPriority[]> {
        try {
            const response = await axiosInstance.get('/admin/delivery-priority');
            return response.data;
        } catch (e) {
            Sentry.captureException(e, { tags: { source: 'delivery-priority' } });
            throw e;
        }
    }

    /** Replaces one city's list; the order of `entries` is the priority. Returns what the server stored. */
    async save(cityId: number, entries: { provider: DeliveryProvider; enabled: boolean }[]): Promise<CityDeliveryPriority> {
        const response = await axiosInstance.put(`/admin/delivery-priority/${cityId}`, entries);
        return response.data;
    }

    /** Sets, or with a blank value clears, the city's Telegram group. Returns the city as stored. */
    async setTelegram(cityId: number, chatId: string): Promise<CityDeliveryPriority> {
        const response = await axiosInstance.put(`/admin/delivery-priority/${cityId}/telegram`, { chatId });
        return response.data;
    }
}
